import PropTypes from 'prop-types';
import styled from 'styled-components';

import { config } from '_config';
import { theme } from '_constants/theme';

import * as Layout from '_components/atomic/layouts';
import JoinBanner from '../JoinBanner';

export const understandingDifferencesBetweenLLCAndSCorpContents = [
  'What is an LLC?',
  'What is an S-Corp?',
  'Key Differences Between LLC and S-Corp',
  ' Taxation',
  ' Ownership and Structure',
  ' Compliance Requirements',
  ' Profit Distribution',
  'What is right for your business?',
];

const StyledContainer = styled.div`
  position: relative;
`;

const StyledBlock = styled.div`
  position: relative;
  z-index: 1;

  ol,
  ul {
    padding-top: ${theme.spaces.regular}px;

    li {
      margin-bottom: ${theme.spaces.regular}px;

      &::marker {
        font-weight: 700;
      }
    }
  }
`;

const StyledBackgroundImageContainer = styled.div`
  position: absolute;
  top: 40%;
  width: 100%;

  img {
    width: 100%;
  }
`;

const StyledImageContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${theme.spaces.large4X}px;
`;

const UnderstandingDifferencesBetweenLLCAndSCorpArticle = ({ idPrefix }) => {
  return (
    <StyledContainer>
      <Layout.Card.Text>
        Choosing the right business structure is a crucial decision for
        entrepreneurs. Two popular options are the Limited Liability Company
        (LLC) and the S Corporation (S-Corp). While both offer distinct
        advantages, understanding their differences is essential for making an
        informed decision that best suits your business needs. This guide will
        help you understand the key differences between an LLC and an S-Corp.
      </Layout.Card.Text>
      <StyledBlock id={`${idPrefix}-${0}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          What is an LLC?
        </Layout.Card.Text>
        <Layout.Card.Text>
          A Limited Liability Company (LLC) is a flexible business structure
          that combines the limited liability protection of a corporation with
          the tax benefits and operational simplicity of a partnership. It is
          popular among small business owners due to its ease of formation and
          management.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${1}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          What is an S-Corp?
        </Layout.Card.Text>
        <Layout.Card.Text>
          An S Corporation (S-Corp) is a special tax designation granted by the
          IRS that allows corporations to pass corporate income, losses,
          deductions, and credits through to their shareholders for federal tax
          purposes. This can result in significant tax savings and advantages.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${2}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Key Differences Between LLC and S-Corp
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${3}`}>
        <Layout.Card.Text
          as="h2"
          size={theme.fontSizes.large}
          boldest
          centered
          underline
        >
          Taxation
        </Layout.Card.Text>
        <Layout.Card.Text>
          <b>LLC Taxation:</b>
          <ul>
            <li>
              An LLC offers pass-through taxation by default, meaning profits
              and losses are reported on the individual tax returns of the
              owners (members).
            </li>
            <li>
              LLCs can also choose to be taxed as a sole proprietorship,
              partnership, C-Corp, or S-Corp, depending on the business&apos;s
              needs.
            </li>
          </ul>
          <b>LLC Taxation:</b>
          <ul>
            <li>
              An S-Corp also benefits from pass-through taxation, but it avoids
              the self-employment taxes that LLC members must pay on all
              profits.
            </li>
            <li>
              Only salaries paid to owners are subject to self-employment taxes,
              while distributions (dividends) are not.
            </li>
          </ul>
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${4}`}>
        <Layout.Card.Text
          as="h2"
          size={theme.fontSizes.large}
          boldest
          centered
          underline
        >
          Ownership and Structure
        </Layout.Card.Text>
        <Layout.Card.Text>
          <b>LLC Ownership and Structure:</b>
          <ul>
            <li>
              LLCs offer flexibility in ownership and management. They can have
              an unlimited number of members, including individuals,
              corporations, and other LLCs.
            </li>
            <li>
              Management can be member-managed or manager-managed, allowing
              owners to choose how they want to run the business.
            </li>
          </ul>
          <b>S-Corp Ownership and Structure:</b>
          <ul>
            <li>
              S-Corps have more restrictions on ownership. They can have no more
              than 100 shareholders, and shareholders must be U.S. citizens or
              residents.
            </li>
            <li>
              S-Corps are required to have a board of directors, hold annual
              meetings, and follow strict corporate formalities.
            </li>
          </ul>
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${5}`}>
        <Layout.Card.Text
          as="h2"
          size={theme.fontSizes.large}
          boldest
          centered
          underline
        >
          Compliance Requirements
        </Layout.Card.Text>
        <Layout.Card.Text>
          <b>LLC Compliance Requirements:</b>
          <ul>
            <li>
              LLCs have fewer ongoing compliance requirements compared to
              S-Corps. They are not required to hold annual meetings or maintain
              a board of directors.
            </li>
            <li>
              LLCs must file an annual report and pay any necessary state fees.
            </li>
          </ul>
          <b>S-Corp Compliance Requirements:</b>
          <ul>
            <li>
              S-Corps must adhere to corporate formalities, such as holding
              annual shareholder meetings, keeping detailed records (minutes),
              and maintaining a board of directors.
            </li>
            <li>
              They are also required to file annual reports and other
              state-specific documents.
            </li>
          </ul>
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${6}`}>
        <Layout.Card.Text
          as="h2"
          size={theme.fontSizes.large}
          boldest
          centered
          underline
        >
          Profit Distribution
        </Layout.Card.Text>
        <Layout.Card.Text>
          <b>LLC Profit Distribution:</b>
          <ul>
            <li>
              LLCs offer flexibility in profit distribution. Members can
              distribute profits in any manner they see fit, regardless of
              ownership percentage.
            </li>
            <li>
              This allows for customized profit-sharing arrangements that
              reflect each member&apos;s contribution to the business.
            </li>
          </ul>
          <b>S-Corp Profit Distribution:</b>
          <ul>
            <li>
              S-Corps must distribute profits and losses according to the
              ownership percentages of the shareholders.
            </li>
            <li>
              This can limit flexibility in profit distribution compared to an
              LLC.
            </li>
          </ul>
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${7}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Which is Right for Your Business?
        </Layout.Card.Text>
        <Layout.Card.Text>
          Deciding between an LLC and an S-Corp depends on your business&apos;s
          specific needs and goals. Consider the following factors:
          <ul>
            <li>
              <b>Tax Considerations:</b> If minimizing self-employment taxes is
              a priority, an S-Corp might be more advantageous. However, the
              flexibility of an LLC&apos;s taxation options can also be
              beneficial.
            </li>
            <li>
              <b>Ownership Structure:</b> If you want flexibility in ownership
              and profit distribution, an LLC may be a better choice. S-Corps
              have more restrictions but can offer tax benefits.
            </li>
            <li>
              <b>Compliance and Formalities:</b> LLCs require less ongoing
              compliance, making them easier to manage. S-Corps require
              adherence to corporate formalities, which can be more burdensome.
            </li>
          </ul>
        </Layout.Card.Text>
      </StyledBlock>
      <Layout.Card.Text>
        Understanding the differences between an LLC and an S-Corp is essential
        for choosing the right business structure. Both offer unique benefits
        and can be tailored to meet your business&apos;s needs. By considering
        taxation, ownership, compliance, and profit distribution, you can make
        an informed decision that supports your business&apos;s success.
      </Layout.Card.Text>
      <JoinBanner />
      <StyledBackgroundImageContainer>
        <picture>
          <source
            srcSet={`${config.basePath}/images/blog-arrow-logo2.svg`}
            type="image/svg+xml"
          />
          <img
            src={`${config.basePath}/images/blog-arrow-logo2.svg`}
            alt="Background Logo"
          />
        </picture>
      </StyledBackgroundImageContainer>
    </StyledContainer>
  );
};

UnderstandingDifferencesBetweenLLCAndSCorpArticle.propTypes = {
  idPrefix: PropTypes.string,
};

export default UnderstandingDifferencesBetweenLLCAndSCorpArticle;
